import { getApiBaseUrl, getUsApiBaseUrl } from './base-url';

let sampleWorkspaceId: number | null = null;

/**
 * Records the sample workspace, so requests scoped to it go to the US data center. Set from the
 * sample-accounts read the app shell makes at boot; `null` when the account has none.
 */
export function setSampleWorkspaceId(workspaceId: number | null | undefined) {
  sampleWorkspaceId = workspaceId ?? null;
}

const ENVIRONMENT_PATH = /^\/v1\/environments\/(\d+)(?:[/?#]|$)/;

function isSampleWorkspacePath(path: string) {
  const workspaceId = ENVIRONMENT_PATH.exec(path)?.[1];
  return workspaceId != null && sampleWorkspaceId != null && Number(workspaceId) === sampleWorkspaceId;
}

/**
 * Moves a request scoped to the sample workspace onto the US host. The sample workspace lives only
 * in the US data center, so an EU account viewing it can't use its regional host. It's read-only,
 * so no EU customer data crosses regions. Mirrors Ember's `ApplicationAdapter.buildURL` and the
 * `api` service's `buildURL` (GRO-2123).
 *
 * Anything else, and every request from a US account or a local build, where both hosts are the
 * same, is returned unchanged.
 */
export function toSampleWorkspaceHost(url: string): string {
  const regional = getApiBaseUrl();
  const us = getUsApiBaseUrl();
  if (regional === us || !url.startsWith(regional)) {
    return url;
  }

  const path = url.slice(regional.length);
  return isSampleWorkspacePath(path) ? `${us}${path}` : url;
}

/** Wraps a fetch so requests scoped to the sample workspace go to the US host. */
export function withSampleWorkspaceHost(fetchFn: typeof globalThis.fetch | undefined): typeof globalThis.fetch {
  return (input, init) => {
    const target = typeof input === 'string' ? toSampleWorkspaceHost(input) : input;
    return (fetchFn ?? globalThis.fetch)(target, init);
  };
}

/** The global fetch, with requests scoped to the sample workspace sent to the US host. */
export const sampleWorkspaceFetch = withSampleWorkspaceHost(undefined);
